/**
 * Subdomain Router Utility
 * Detects the current subdomain and returns the appropriate app type.
 *
 * Handles both production and staging:
 *   Production:  app.table-tap.in, restaurant.table-tap.in, admin.table-tap.in
 *   Staging:     staging.app.table-tap.in, staging.restaurant.table-tap.in, etc.
 */

/** localhost, loopback, and RFC1918 LAN IPs (phone-on-WiFi dev testing) */
export const isLocalDevHostname = (
  hostname = typeof window !== 'undefined' ? window.location.hostname : '',
) => {
  const h = (hostname || '').toLowerCase();
  if (h === 'localhost' || h === '127.0.0.1') return true;
  if (/^192\.168\.\d{1,3}\.\d{1,3}$/.test(h)) return true;
  if (/^10\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(h)) return true;
  if (/^172\.(1[6-9]|2\d|3[01])\.\d{1,3}\.\d{1,3}$/.test(h)) return true;
  return false;
};

/**
 * Hosts that use path-based routing (/restaurant, /admin) instead of subdomains.
 * Includes localhost and the Vercel dev deployment (table-tap-app.vercel.app).
 */
export const isPathBasedRoutingHost = (
  hostname = typeof window !== 'undefined' ? window.location.hostname : '',
) => {
  const h = (hostname || '').toLowerCase();
  return isLocalDevHostname(h) || h.endsWith('.vercel.app');
};

function resolveSubdomainFromPath(pathname = '') {
  if (pathname.startsWith('/owner')) return 'owner';
  if (pathname.startsWith('/restaurant')) return 'restaurant';
  if (pathname.startsWith('/admin')) return 'admin';
  if (pathname.startsWith('/app')) return 'app';
  return 'app';
}

/**
 * Strip deployment prefixes (staging, preview) so portal checks work on
 * staging.admin.table-tap.in the same as admin.table-tap.in.
 */
export const stripDeploymentPrefix = (hostname) => {
  return (hostname || '').toLowerCase().replace(/^(staging|preview)\./i, '');
};

export const isAdminHost = (
  hostname = typeof window !== 'undefined' ? window.location.hostname : '',
) => {
  return stripDeploymentPrefix(hostname).startsWith('admin.');
};

export const isRestaurantPortalHost = (
  hostname = typeof window !== 'undefined' ? window.location.hostname : '',
) => {
  const h = stripDeploymentPrefix(hostname);
  return h.startsWith('restaurant.') || h.startsWith('manager.') || h.startsWith('owner.');
};

/** True on admin.* or restaurant/manager/owner.* hosts (prod or staging). */
export const isPortalHost = (hostname) => {
  return isAdminHost(hostname) || isRestaurantPortalHost(hostname);
};

/** Customer app origin — not a staff/admin portal subdomain. */
export const isCustomerAppOrigin = (
  hostname = typeof window !== 'undefined' ? window.location.hostname : '',
) => {
  return !isPortalHost(hostname);
};

/** Path-based portal detection (localhost / Vercel preview use /admin, /restaurant, etc.) */
export const isAdminPath = (
  pathname = typeof window !== 'undefined' ? window.location.pathname : '',
) => {
  return (pathname || '').startsWith('/admin');
};

export const isRestaurantPath = (
  pathname = typeof window !== 'undefined' ? window.location.pathname : '',
) => {
  const p = pathname || '';
  return p.startsWith('/restaurant') || p.startsWith('/owner');
};

/** Admin portal — subdomain (admin.*) or path (/admin/*) on shared hosts. */
export const isAdminContext = (
  hostname = typeof window !== 'undefined' ? window.location.hostname : '',
  pathname = typeof window !== 'undefined' ? window.location.pathname : '',
) => {
  return isAdminHost(hostname) || isAdminPath(pathname);
};

/** Restaurant/owner portal — subdomain or path prefix. */
export const isRestaurantPortalContext = (
  hostname = typeof window !== 'undefined' ? window.location.hostname : '',
  pathname = typeof window !== 'undefined' ? window.location.pathname : '',
) => {
  return isRestaurantPortalHost(hostname) || isRestaurantPath(pathname);
};

export const getAdminHomePath = (
  hostname = typeof window !== 'undefined' ? window.location.hostname : '',
) => {
  return isAdminHost(hostname) ? '/' : '/admin';
};

export const getRestaurantDashboardPath = (
  hostname = typeof window !== 'undefined' ? window.location.hostname : '',
  pathname = typeof window !== 'undefined' ? window.location.pathname : '',
) => {
  if (isRestaurantPortalHost(hostname)) return '/dashboard';
  if ((pathname || '').startsWith('/owner')) return '/owner/dashboard';
  return '/restaurant/dashboard';
};

export const getPortalLoginPath = (
  hostname = typeof window !== 'undefined' ? window.location.hostname : '',
  pathname = typeof window !== 'undefined' ? window.location.pathname : '',
) => {
  if (isAdminContext(hostname, pathname)) {
    return isAdminHost(hostname) ? '/login' : '/admin/login';
  }
  if (isRestaurantPortalContext(hostname, pathname)) {
    if (isRestaurantPortalHost(hostname)) return '/login';
    return pathname.startsWith('/owner') ? '/owner/login' : '/restaurant/login';
  }
  return '/login';
};

/**
 * Extracts the logical app type from a hostname regardless of staging prefix.
 * e.g. "staging.app.table-tap.in" → "app"
 *      "restaurant.table-tap.in"  → "restaurant"
 */
const resolveAppType = (hostname) => {
  const parts = hostname.toLowerCase().split('.');
  // Walk subdomain parts (everything before the root domain) left→right
  // and return the first recognised app-type keyword.
  for (const part of parts) {
    if (part === 'admin') return 'admin';
    if (part === 'owner') return 'owner';
    if (part === 'restaurant' || part === 'manager') return 'restaurant';
    if (part === 'app') return 'app';
    // Skip non-keyword prefixes like "staging", "preview", etc.
  }
  // Fallback: substring match (catches Vercel preview URLs)
  if (hostname.includes('admin')) return 'admin';
  if (hostname.includes('owner')) return 'owner';
  if (hostname.includes('restaurant') || hostname.includes('manager')) return 'restaurant';
  if (hostname.includes('app')) return 'app';
  return 'app';
};

/**
 * Get the current subdomain from window.location
 * @returns {'landing' | 'app' | 'restaurant' | 'admin' | 'owner'} The app type based on subdomain
 */
export const getSubdomain = () => {
  const hostname = window.location.hostname;

  // Path-based routing (localhost + Vercel dev deployment)
  if (isPathBasedRoutingHost(hostname)) {
    const params = new URLSearchParams(window.location.search);
    const devSubdomain = params.get('subdomain');
    if (devSubdomain) return devSubdomain;

    return resolveSubdomainFromPath(window.location.pathname);
  }

  return resolveAppType(hostname);
};

/**
 * Get the base path for the current subdomain (path-based hosts only)
 */
export const getBasePath = () => {
  if (!isPathBasedRoutingHost(window.location.hostname)) return '';

  const subdomain = getSubdomain();

  switch (subdomain) {
    case 'app':
      return '/app';
    case 'restaurant':
      return '/restaurant';
    case 'admin':
      return '/admin';
    case 'owner':
      return '/owner';
    default:
      return '';
  }
};

/**
 * Check if current environment uses path-based routing (localhost / Vercel dev).
 */
export const isDevelopment = () => {
  return isPathBasedRoutingHost(window.location.hostname);
};

/**
 * Returns true when on a staging deployment (staging.*.table-tap.in)
 */
export const isStaging = () => {
  const hostname = window.location.hostname;
  return hostname.startsWith('staging.') && hostname.endsWith('table-tap.in');
};

/**
 * Get the appropriate URL for cross-subdomain navigation.
 * Preserves any staging prefix so links between apps work on staging too.
 *
 * e.g. on staging.app.table-tap.in:
 *   getSubdomainUrl('restaurant') → https://staging.restaurant.table-tap.in/
 *
 * @param {string} targetSubdomain - The app type to navigate to ('app', 'restaurant', 'admin', 'owner')
 * @param {string} path - The path within that subdomain
 */
export const getSubdomainUrl = (targetSubdomain, path = '/') => {
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;

  // Path-based routing (localhost + Vercel dev deployment)
  if (isPathBasedRoutingHost(hostname)) {
    const port = window.location.port ? `:${window.location.port}` : '';
    const origin = `${protocol}//${hostname}${port}`;
    switch (targetSubdomain) {
      case 'app':
        return `${origin}${path}`;
      case 'restaurant':
        return `${origin}/restaurant${path}`;
      case 'admin':
        return `${origin}/admin${path}`;
      case 'owner':
        return `${origin}/owner${path}`;
      default:
        return `${origin}${path}`;
    }
  }

  // Production/staging — derive the root domain and any staging prefix
  const parts = hostname.split('.');

  // Find root domain: last two parts that contain the actual domain name
  // e.g. ['staging','app','table-tap','in'] → rootDomain = 'table-tap.in'
  // e.g. ['app','table-tap','in']           → rootDomain = 'table-tap.in'
  const knownAppParts = new Set([
    'staging',
    'preview',
    'app',
    'restaurant',
    'admin',
    'owner',
    'manager',
  ]);
  const rootParts = [];
  let passedAppPart = false;
  for (let i = parts.length - 1; i >= 0; i--) {
    const p = parts[i].toLowerCase();
    if (!passedAppPart && !knownAppParts.has(p)) {
      rootParts.unshift(parts[i]);
    } else {
      passedAppPart = true;
      // Once we hit an app-type or prefix, stop collecting root parts
      break;
    }
  }
  // Fallback: use last 2 parts as root domain
  const rootDomain = rootParts.length >= 2 ? rootParts.join('.') : parts.slice(-2).join('.');

  // Detect staging prefix (e.g. "staging")
  const stagingPrefix = isStaging() ? `${parts[0]}.` : '';

  if (targetSubdomain === 'landing') {
    return `${protocol}//${stagingPrefix}${rootDomain}${path}`;
  }

  return `${protocol}//${stagingPrefix}${targetSubdomain}.${rootDomain}${path}`;
};

export default {
  getSubdomain,
  getBasePath,
  isLocalDevHostname,
  isPathBasedRoutingHost,
  isDevelopment,
  isStaging,
  getSubdomainUrl,
  stripDeploymentPrefix,
  isAdminHost,
  isRestaurantPortalHost,
  isPortalHost,
  isCustomerAppOrigin,
  isAdminPath,
  isRestaurantPath,
  isAdminContext,
  isRestaurantPortalContext,
  getAdminHomePath,
  getRestaurantDashboardPath,
  getPortalLoginPath,
};
